// AppLogin.js
import React, { useEffect, useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { useMutation} from '@apollo/client';
import HomeplusBigLogo from '../../asset/image/homeplus.svg';
import VzmTextField from '../../common/VzmTextField';
import { VzmInputBlock } from '../../common/VzmStyled';
import { ApiMsg, AppObject, AppStyle, AppWord } from '../../value/AppConst';
import InputUtil from '../../value/InputUtil';
import { useAlertSetter } from '../../api/app-hook';
import UserSessionInfo from '../../value/UserSessionInfo';
import { useSnackbar } from 'notistack';
import UserAllocControl from '../user_alloc/UserAllocControl';
import UserAllocCheck from '../user_alloc/UserAllocCheck';
import UserInfoPasswordOffline from '../user_info/UserInfoPasswordOffline';
import ForgotPassword from './ForgotPassword';
import ValueMisc from '../../value/ValueMisc';
import { LOGIN_NEW_TOP, LOGIN_AS } from './AppLoginGql';
import { AppLoginDict } from './AppLoginDict';

const LogoWidth = 360;
const LogoRightMargin = 40;
const LoginBoxWidth = 520;

const OutterBox = styled(Box)({
    display: 'flex', flexDirection:'column', justifyContent: 'center', alignItems: 'center', height: '100vh',
});

const InnerBox = styled(Box)({
    display: 'inline-flex', width: (LoginBoxWidth + LogoWidth + LogoRightMargin), marginBottom: 100,
});

const LogoBox = styled(Box)({flexGrow: 1,});

const LoginBox = styled(Box)({
    width: LoginBoxWidth, display: 'flex', flexDirection: 'column',
});

const TopText = styled(Box)({
    fontSize: 28, fontWeight: 'bold', fontFamily: 'Times New Roman', fontStyle: 'italic',
});

const ButtonBlock = styled(Box)({
    display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20,
});

const SubButton = styled(Button)({
    margin: '0px 5px 0px 5px', flexGrow: 1,
    '&:first-of-type': {marginLeft: 0},
    '&:last-of-type': {marginRight: 0},
});

const inputMap = AppLoginDict.inputMap;

/**
 * 사용자 로그인 화면.
 * 로그인 여부의 확인은 상위 컴포넌트에서 처리한다.
 * 로그인 성공하면 상위 컴포넌트로 확인 메시지 전달한다.
 * @returns 
 */
export default function AppLogin({
    onLoginSuccess
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [checkedSaveId, setCheckedSaveId] = useState(false);
    const [openUserAlloc, setOpenUserAlloc] = useState(false);
    const [openUserAllocCheck, setOpenUserAllocCheck] = useState(false);
    const [openChangePasswordOffline, setOpenChangePasswordOffline] = useState(false);
    const [openForgotPassword, setOpenForgotPassword] = useState(false);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [loginNewTop, responseLogin] = useMutation(LOGIN_NEW_TOP, {
        onCompleted: (data) => {onCompleteLogin(data)},
        onError: (error) => {addAlert({title:"로그인 중 오류", message:[error.message]})},
    });

    const [loginAs, responseLoginAs] = useMutation(LOGIN_AS, {
        onCompleted: (data) => {onCompleteLogin(data)},
        onError: (error) => {addAlert({title:"로그인(as) 중 오류", message:[error.message]})},
    });

    useEffect(() => {
        const localObj = ValueMisc.readLocalStorageValue(AppWord.OPTION_LOCALSTORAGE_KEY, AppWord.KEY_SAVE_ID);

        if(localObj && localObj.checkedSaveId && localObj.userId) {
            const newInput = {...input};
            newInput.data.userId = localObj.userId;
            newInput.changed = true;
            setInput(newInput);
            setCheckedSaveId(true);
        }
        else {
            console.log('AppLogin: no saved id');
            setCheckedSaveId(false);
        }
    }, []);

    const onCompleteLogin = (data) => {
        const res = data.loginAs ? data.loginAs : data.loginNewTop;
        if(res) {
            if(res.sessionNote === ApiMsg.OK) {
                const sessionInfo = UserSessionInfo.initFromUserObject(res);
                // userInfoRepo(sessionInfo);
                enqueueSnackbar(sessionInfo.user.userName + '님 환영합니다.', {variant: 'success'});
                onLoginSuccess(sessionInfo);
            }
            else {
                addAlert({
                    title: '로그인 실패',
                    message: [
                        '로그인에 실패했습니다.',
                        res.sessionNote
                    ]
                })
            }
        }
    };

    const onChangeValue = (id, value, error) => {
        setInput(InputUtil.reflectData(input, id, value, error, inputMap, false));
    };

    const onClickLogin = () => {
        if(!input.data.userId) return;
        const saveIdObj = {
            userId: checkedSaveId ? input.data.userId : '',
            checkedSaveId: checkedSaveId,
        };
        // 사용자 아이디가 [adminId].as_user.[userId] 형태인 경우, 로그인 as 처리한다.
        const userIdParts = input.data.userId.split('.as_user.');
        const param = userIdParts.length === 2
            ? {variables: {userId: userIdParts[1], adminId: userIdParts[0], adminPassword: input.data.password}}
            : {variables: {userId: input.data.userId, password: input.data.password}};
        const newInput = {...input};
        newInput.data.password = '';
        setInput(newInput);
        if(userIdParts.length === 2) {
            loginAs(param);
        } else {
            loginNewTop(param);
        }
        ValueMisc.addLocalStorageValue(AppWord.OPTION_LOCALSTORAGE_KEY, AppWord.KEY_SAVE_ID, saveIdObj);
    };

    const onClickDevSetUser = (id, pswd) => {
        const newInput = {...input};
        newInput.data.userId = id;
        newInput.data.password = pswd;
        newInput.changed = true;
        setInput(newInput);
    };

    const onChangeSaveId = (event) => {
        setCheckedSaveId(event.target.checked);
        if(!event.target.checked) {
            const localObj = {
                userId: '',
                checkedSaveId: false,
            }
            ValueMisc.addLocalStorageValue(AppWord.OPTION_LOCALSTORAGE_KEY, AppWord.KEY_SAVE_ID, localObj);
        }
    };

    const renderTextTOP = () => {
        return(
            <TopText>
                <span style={{color:"red"}}>T</span>ransport &nbsp;
                <span style={{color:"orange"}}>O</span>peration &nbsp;
                <span style={{color:"green"}}>P</span>rogram
            </TopText>
        );
    };

    if(openForgotPassword) {
        return(
            <ForgotPassword onClose={()=>setOpenForgotPassword(false)} />   
        )
    }
    return(
        <OutterBox>
            <InnerBox>
                <LogoBox>
                    <img src={HomeplusBigLogo} alt="Homeplus" width={LogoWidth}
                        style={{marginRight: LogoRightMargin}}
                    />
                    {renderTextTOP()}
                </LogoBox>
                <LoginBox>
                    <Box style={{flexGrow:1}}>
                        &nbsp;
                    </Box>
                    <form>
                    <VzmInputBlock>
                        <VzmTextField dict={inputMap.userId}
                            value={input.data.userId}
                            onChange={onChangeValue}
                            fullWidth={true}
                            inputProps={{autoComplete:"userId"}}
                        />
                    </VzmInputBlock>
                    <VzmInputBlock>
                        <VzmTextField dict={inputMap.password}
                            value={input.data.password}
                            onChange={onChangeValue}
                            onEnterKey={()=>{if(input.data.password) onClickLogin()}}
                            fullWidth={true}
                            inputProps={{autoComplete: 'current-password'}}
                        />
                    </VzmInputBlock>
                    <VzmInputBlock>
                        <Button variant={AppStyle.mui.contained} color={AppStyle.mui.primary}
                            size={AppWord.LARGE}
                            onClick={onClickLogin}
                            startIcon={<LoginIcon/>}
                            fullWidth
                            disabled={
                                input.hasError || !input.data.userId || !input.data.password
                                || responseLogin.loading
                            }
                        >
                            로그인
                        </Button>
                    </VzmInputBlock>
                    </form>
                    <Box style={{marginTop: 2, marginBottom: 10}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkedSaveId}
                                    onChange={onChangeSaveId} />
                            }
                            label="아이디 저장하기"
                        />
                    </Box>
                    <ButtonBlock>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.secondary}
                            onClick={()=>setOpenUserAlloc(true)}
                        >
                            승인요청
                        </SubButton>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.success}
                            onClick={()=>setOpenUserAllocCheck(true)}
                        >
                            승인요청 확인
                        </SubButton>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.warning}
                            onClick={()=>setOpenChangePasswordOffline(true)}
                        >
                            사용자 암호 변경
                        </SubButton>
                        <SubButton
                            variant={AppStyle.mui.outlined} color={AppStyle.mui.info}
                            onClick={()=>setOpenForgotPassword(true)}
                        >
                            메일로 암호 재설정
                        </SubButton>
                    </ButtonBlock>
                </LoginBox>
            </InnerBox>
            <UserAllocControl
                open={openUserAlloc}
                onClose={()=>setOpenUserAlloc(false)}
            />
            <UserAllocCheck
                open={openUserAllocCheck}
                onClose={()=>setOpenUserAllocCheck(false)}
            />
            <UserInfoPasswordOffline
                open={openChangePasswordOffline}
                onClose={()=>setOpenChangePasswordOffline(false)}
            />
        </OutterBox>
    );
}

// hmn00	6	916	함안ADC	asdf123